import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { TableUtils, DragScrollConfig } from '../../orders/utils/table.utils';
import {
  buildPaginationData,
  calculateTotalPages,
  applyPageChange,
  applyItemsPerPageChange,
} from '../../orders/utils/orders-page-shared.utils';
import { ApiService } from '../../services/api.service';
import { CompanyService } from '../services/company.service';
import {
  loadCompaniesAndSelectFirst,
  toggleCompanyDropdown,
  closeCompanyDropdown,
} from '../utils/company.util';
import { countActiveFilterValues } from '../utils/filter-count.util';
import {
  PaginationBarComponent,
  PaginationData,
} from '../../shared/components/pagination-bar/pagination-bar.component';
import { EmptyResultComponent } from '../../shared/components/empty-result/empty-result.component';
import {
  LoadingTableShimmerComponent,
  ShimmerColumn,
} from '../../shared/components/loading-table-shimmer/loading-table-shimmer.component';
import { TitleBarComponent } from '../../shared/components/title-bar/title-bar.component';
import { CustomerStatsCardComponent } from '../../components/customer-stats-card/customer-stats-card.component';
import { SearchbarComponent } from '../../shared/components/searchbar/searchbar.component';
import { ActionButtonComponent } from '../../shared/components/action-button/action-button.component';
import {
  FilterModalComponent,
  FilterCategory,
} from '../../shared/components/filter-modal/filter-modal.component';
import {
  SimpleDropdownComponent,
  DropdownOption,
} from '../../shared/components/simple-dropdown/simple-dropdown.component';
import { CustomerStatData, CustomerListItem } from '../../shared/types';

export type Customer = CustomerListItem;

@Component({
  selector: 'app-admin-customers',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PaginationBarComponent,
    EmptyResultComponent,
    LoadingTableShimmerComponent,
    TitleBarComponent,
    CustomerStatsCardComponent,
    SearchbarComponent,
    ActionButtonComponent,
    FilterModalComponent,
    SimpleDropdownComponent,
  ],
  templateUrl: './admin-customers.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./admin-customers.component.scss'],
})
export class AdminCustomersComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('tableScrollWrapper') tableScrollWrapper?: ElementRef<HTMLElement>;

  searchTerm = '';
  itemsPerPage = 10;
  currentPage = 1;
  totalItems = 0;
  loading = true;

  // Company dropdown properties
  companyOptions: DropdownOption[] = [];
  selectedCompanyId = '';
  isCompanyDropdownOpen = false;

  customerStats: CustomerStatData[] = this.getDefaultCustomerStats();

  customers: Customer[] = [];
  showFilterModal = false;

  readonly filterCategories: FilterCategory[] = [
    {
      id: 'status',
      name: 'Status',
      options: [
        { id: 'active', label: 'Active', value: 'active', selected: false },
        { id: 'inactive', label: 'Inactive', value: 'inactive', selected: false },
      ],
    },
  ];

  shimmerColumns: ShimmerColumn[] = [
    { label: 'Name', width: 'md' },
    { label: 'Email', width: 'md' },
    { label: 'Phone', width: 'sm' },
    { label: 'License Plate', width: 'sm' },
    { label: 'Status', width: 'sm' },
    { label: 'Vehicles', width: 'sm' },
    { label: 'Visits', width: 'xs' },
    { label: 'Revenue', width: 'sm' },
    { label: 'Last Visit', width: 'sm' },
  ];

  appliedFilters: any = {
    status: {
      active: false,
      inactive: false,
    },
  };

  // Sorting functionality
  sortColumn: string | null = null;
  sortDirection: 'asc' | 'desc' | 'none' = 'none';

  // Drag scrolling configuration
  private dragScrollConfig!: DragScrollConfig;

  private readonly apiService = inject(ApiService);
  private readonly companyService = inject(CompanyService);
  private readonly messageService = inject(NotificationService);
  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.loadCompanies();
  }

  /**
   * Loads customer statistics for the currently selected company from the super-admin API and updates the component state.
   * @example
   * loadCustomerStatistics()
   * // undefined
   * @param {{void}} none - No arguments.
   * @returns {{void}} Does not return a value; updates component state or logs errors to the console.
   **/
  private loadCustomerStatistics(): void {
    if (!this.selectedCompanyId) {
      return;
    }

    this.apiService
      .getWithSuperAdminToken(
        `admin/super-admin/companies/${this.selectedCompanyId}/customers/statistics`
      )
      .subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            // Try to extract customer statistics from the response
            // The response might have statistics in different formats
            const stats = this.extractCustomerStatsFromResponse(response.data);
            if (stats) {
              this.updateCustomerStats(stats);
            }
          }
        },
        error: error => {
          console.error('Error loading customer statistics:', error);
          // Keep default stats on error
        },
      });
  }

  private extractCustomerStatsFromResponse(data: any): any {
    // The API response structure matches CustomerStatisticsApiResponse
    return data;
  }

  /**
   * Updates the component's customerStats array with formatted values and percent changes from the provided data.
   * @example
   * updateCustomerStats({
   *   revenue_for_today: 1234.56,
   *   revenue_growth_percentage: 5,
   *   visits_this_month: 200,
   *   visit_growth_percentage: -2,
   *   total_customers_this_month: 1500,
   *   customer_growth_percentage: 3,
   *   new_customers_this_month: 50,
   *   new_customers_growth_percentage: 10
   * })
   * undefined
   * @param {{any}} {{data}} - Data object containing revenue, visits, total and new customer counts and their growth percentages.
   * @returns {{void}} Void return; updates internal customerStats state in place.
   **/
  private updateCustomerStats(data: any): void {
    const formatValue = (value: string | number | null | undefined): string => {
      if (value === null || value === undefined) return '0';
      return value.toString();
    };

    const formatPercentage = (value: string | number | null | undefined): string => {
      if (!value && value !== 0) return '0%';
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      return numValue >= 0 ? `+${numValue}%` : `${numValue}%`;
    };

    // Update Today's Revenue
    if (data.revenue_for_today !== undefined) {
      this.customerStats[0].value = `$${formatValue(data.revenue_for_today)}`;
      this.customerStats[0].percentage = formatPercentage(data.revenue_growth_percentage);
    }

    // Update Average Visits
    if (data.visits_this_month !== undefined) {
      this.customerStats[1].value = formatValue(data.visits_this_month);
      this.customerStats[1].percentage = formatPercentage(data.visit_growth_percentage);
    }

    // Update Total Customers
    if (data.total_customers_this_month !== undefined) {
      this.customerStats[2].value = formatValue(data.total_customers_this_month);
      this.customerStats[2].percentage = formatPercentage(data.customer_growth_percentage);
    }

    // Update New Customers
    if (data.new_customers_this_month !== undefined) {
      this.customerStats[3].value = formatValue(data.new_customers_this_month);
      this.customerStats[3].percentage = formatPercentage(data.new_customers_growth_percentage);
    }
  }

  ngAfterViewInit(): void {
    this.dragScrollConfig =
      TableUtils.reinitializeDragScrolling(this.tableScrollWrapper) ?? this.dragScrollConfig;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    TableUtils.cleanupTableScrolling();
  }

  private loadCompanies(): void {
    loadCompaniesAndSelectFirst(this.companyService, this.messageService, {
      setCompanyOptions: opts => {
        this.companyOptions = opts;
      },
      setSelectedCompanyId: id => {
        this.selectedCompanyId = id;
      },
      onFirstSelected: () => {
        this.loadCustomers();
        this.loadCustomerStatistics();
      },
      onComplete: () => {
        if (this.companyOptions.length === 0) {
          this.loading = false;
        }
      },
    });
  }

  toggleCompanyDropdown(): void {
    toggleCompanyDropdown(this);
  }
  closeCompanyDropdown(): void {
    closeCompanyDropdown(this);
  }

  /**
   * Selects a company by ID, updates component state, and loads or clears customer data accordingly.
   * @example
   * electCompany('abc123')
   * void
   * @param {{string}} {{companyId}} - ID of the company to select; if falsy, clears selection and resets customer data.
   * @returns {{void}} Does not return a value; updates component state and triggers loading or clearing of customer data.
   **/
  selectCompany(companyId: string): void {
    this.selectedCompanyId = companyId || '';
    this.isCompanyDropdownOpen = false;
    this.currentPage = 1;
    if (companyId) {
      this.loadCustomers();
      this.loadCustomerStatistics();
    } else {
      this.customers = [];
      this.totalItems = 0;
      this.customerStats = this.getDefaultCustomerStats();
    }
  }

  private getDefaultCustomerStats(): CustomerStatData[] {
    return [
      {
        title: "Today's Revenue",
        value: '$0',
        percentage: '0%',
        periodText: 'this month',
        gradient: 'linear-gradient(180deg, #A9DF85 0%, #6DA548 100%)',
        icon: 'assets/customer/c-1.png',
      },
      {
        title: 'Average Visits',
        value: '0',
        percentage: '0%',
        periodText: 'this month',
        gradient: 'linear-gradient(180deg, #AFA5F6 0%, #8878F2 100%)',
        icon: 'assets/customer/c-2.png',
      },
      {
        title: 'Total Customers',
        value: '0',
        percentage: '0%',
        periodText: 'this month',
        gradient: 'linear-gradient(180deg, #A3C7FF 0%, #5186D9 100%)',
        icon: 'assets/customer/c-3.png',
      },
      {
        title: 'New Customers',
        value: '0',
        percentage: '0%',
        periodText: 'this month',
        gradient: 'linear-gradient(180deg, #77D7EA 0%, #42ADC0 100%)',
        icon: 'assets/customer/c-4.png',
      },
    ];
  }

  /**
   * Load customers for the currently selected company using current pagination, search term and applied filters; updates component state, reinitializes drag scrolling and handles errors.
   * @example
   * loadCustomers()
   * void
   * @returns {void} Triggers an API call to fetch customers and updates properties like customers, totalItems and loading.
   */
  private loadCustomers(): void {
    if (!this.selectedCompanyId) {
      return;
    }

    this.loading = true;
    this.customers = [];
    this.totalItems = 0;

    const params: Record<string, any> = {
      page: this.currentPage.toString(),
      limit: this.itemsPerPage.toString(),
    };

    if (this.searchTerm) {
      params['search'] = this.searchTerm;
    }

    // Add filters
    const statusFilters: string[] = [];
    if (this.appliedFilters.status.active) statusFilters.push('active');
    if (this.appliedFilters.status.inactive) statusFilters.push('inactive');

    if (statusFilters.length > 0) {
      params['customer_status'] = statusFilters.join(',');
    }

    this.apiService
      .getWithSuperAdminToken(
        `admin/super-admin/companies/${this.selectedCompanyId}/customers`,
        params
      )
      .subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            if (response.data.customers && Array.isArray(response.data.customers)) {
              this.customers = this.mapApiCustomersToCustomers(response.data.customers);
              this.totalItems =
                response.data.pagination?.total ||
                response.data.totalCustomers ||
                this.customers.length;
            } else if (Array.isArray(response.data)) {
              this.customers = this.mapApiCustomersToCustomers(response.data);
              this.totalItems =
                response.total || response.totalItems || response.count || this.customers.length;
            }
          }
          this.loading = false;
          setTimeout(() => {
            this.dragScrollConfig =
              TableUtils.reinitializeDragScrolling(this.tableScrollWrapper) ??
              this.dragScrollConfig;
          }, 100);
        },
        error: error => {
          console.error('Error loading customers:', error);
          this.loading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load customers',
          });
        },
      });
  }

  /**
   * Map an array of raw API customer objects into an array of normalized Customer objects with defaults and derived fields.
   * @example
   * mapApiCustomersToCustomers([{ id: 1, first_name: 'John', last_name: 'Doe', email: 'j@example.com', vehicle: { make: 'Toyota' }, visit_count: 3, revenue: 120 }])
   * [{ id: 1, first_name: 'John', last_name: 'Doe', email: 'j@example.com', contact: '', type: 'regular', license_plate: '', customer_status: 'active', created_at: '', updated_at: '', name: 'John Doe', vehicles: 'Toyota', licensePlates: '-', numberOfVisits: 3, revenue: '$120', lastVisit: '-' }]
   * @param {any[]} apiCustomers - Array of raw customer objects returned by the API to be transformed.
   * @returns {Customer[]} Array of normalized Customer objects ready for use in the application.
   **/
  private mapApiCustomersToCustomers(apiCustomers: any[]): Customer[] {
    return apiCustomers.map(apiCustomer => ({
      id: apiCustomer.id,
      first_name: apiCustomer.first_name || '',
      last_name: apiCustomer.last_name || null,
      email: apiCustomer.email || '',
      contact: apiCustomer.contact || '',
      type: apiCustomer.type || 'regular',
      license_plate: apiCustomer.license_plate || '',
      customer_status: apiCustomer.customer_status || 'active',
      created_at: apiCustomer.created_at || '',
      updated_at: apiCustomer.updated_at || '',
      name: this.getFullName(apiCustomer.first_name, apiCustomer.last_name),
      vehicles: apiCustomer.vehicle?.make || '-',
      licensePlates: apiCustomer.license_plate || '-',
      numberOfVisits: apiCustomer.visit_count || apiCustomer.visits || 0,
      revenue: apiCustomer.revenue ? `${apiCustomer.revenue}` : '$0',
      lastVisit: apiCustomer.last_visit_date || '-',
    }));
  }

  private getFullName(firstName: string, lastName: string | null): string {
    if (!firstName) return '';
    if (!lastName) return firstName;
    return `${firstName} ${lastName}`;
  }

  onSearchChange(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.currentPage = 1;
    this.loadCustomers();
  }

  onFilter(): void {
    this.showFilterModal = true;
  }

  onCloseFilterModal(): void {
    this.showFilterModal = false;
  }

  /**
   * Build and apply filters from the selected options of the provided categories, update component state, close the filter modal, and reload the customer list.
   * @example
   * nFilterModalApply([{ id: 'status', options: [{ value: 'active', selected: true }, { value: 'inactive', selected: false }] }])
   * undefined
   * @param {FilterCategory[]} categories - Array of filter category objects whose selected options will be converted into applied filters.
   * @returns {void} No return value; side effects include setting appliedFilters, resetting currentPage to 1, hiding the filter modal, and calling loadCustomers().
   */
  onFilterModalApply(categories: FilterCategory[]): void {
    const filters: any = {
      status: {
        active: false,
        inactive: false,
      },
    };

    categories.forEach(category => {
      const selectedOptions = category.options.filter(option => option.selected);

      switch (category.id) {
        case 'status':
          selectedOptions.forEach(option => {
            if (option.value === 'active') filters.status.active = true;
            if (option.value === 'inactive') filters.status.inactive = true;
          });
          break;
      }
    });

    this.appliedFilters = filters;
    this.currentPage = 1;
    this.showFilterModal = false;
    this.loadCustomers();
  }

  get hasActiveFilters(): boolean {
    return this.getActiveFilterCount() > 0;
  }

  getActiveFilterCount(): number {
    return countActiveFilterValues(this.appliedFilters.status);
  }

  get hasSearchOrFilters(): boolean {
    return (Boolean(this.searchTerm) && this.searchTerm.trim().length > 0) || this.hasActiveFilters;
  }

  get paginationData(): PaginationData {
    return buildPaginationData(
      this.currentPage,
      this.totalPages,
      this.totalItems,
      this.itemsPerPage
    );
  }

  get totalPages(): number {
    return calculateTotalPages(this.totalItems, this.itemsPerPage);
  }

  onPageChange(page: number): void {
    applyPageChange(
      {
        totalPages: this.totalPages,
        setCurrentPage: p => {
          this.currentPage = p;
        },
        loadOrders: () => this.loadCustomers(),
      },
      page
    );
  }

  onItemsPerPageChange(newItemsPerPage: number): void {
    applyItemsPerPageChange(
      {
        setItemsPerPage: v => {
          this.itemsPerPage = v;
        },
        setCurrentPage: p => {
          this.currentPage = p;
        },
        loadOrders: () => this.loadCustomers(),
      },
      newItemsPerPage
    );
  }

  /**
   * Toggle sorting by the customer name column and sort the customers list.
   * @example
   * ortByName()
   * // sorts this.customers by name (case-insensitive) and updates this.sortColumn and this.sortDirection
   * @returns {void} Updates component state; does not return a value.
   **/
  sortByName(): void {
    if (this.sortColumn === 'name') {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = 'name';
      this.sortDirection = 'asc';
    }
    this.customers = [...this.customers].sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      const comparison = nameA.localeCompare(nameB);
      return this.sortDirection === 'asc' ? comparison : -comparison;
    });
  }

  /**
   * Toggle sorting of the customers list by email and update sorting state.
   * @example
   * ortByEmail()
   * // undefined
   * @returns {void} Updates this.customers, this.sortColumn, and this.sortDirection; no return value.
   **/
  sortByEmail(): void {
    if (this.sortColumn === 'email') {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = 'email';
      this.sortDirection = 'asc';
    }
    this.customers = [...this.customers].sort((a, b) => {
      const comparison = a.email.localeCompare(b.email);
      return this.sortDirection === 'asc' ? comparison : -comparison;
    });
  }

  /**
   * Return the icon URL for a table column based on the component's current sort column and direction.
   * @example
   * etSortIcon('name')
   * '/assets/images/sort-ascending-numbers.svg'
   * @param {{string}} {{column}} - Column key to determine which sort icon to return.
   * @returns {{string}} Return path to the sort icon image.
   **/
  getSortIcon(column: string): string {
    if (this.sortColumn === column) {
      const iconMap: Record<string, string> = {
        name:
          this.sortDirection === 'asc'
            ? '/assets/images/sort-ascending-numbers.svg'
            : '/assets/images/sort-descending-numbers.svg',
        email:
          this.sortDirection === 'asc'
            ? '/assets/images/sort-ascending-numbers.svg'
            : '/assets/images/sort-descending-numbers.svg',
      };
      return iconMap[column] || '/assets/images/tabler_arrows_sort.png';
    }
    return '/assets/images/tabler_arrows_sort.png';
  }

  getStatusClass(status: string): string {
    if (status === 'active') {
      return 'status-green';
    } else if (status === 'inactive') {
      return 'status-gray';
    }
    return 'status-gray';
  }

  /**
   * Format a last-visit date string into 'Mon DD, YYYY' (e.g., "Mar 05, 2023"); returns '-' for empty indicators or the original input if it cannot be parsed.
   * @example
   * formatLastVisitDate('2023-03-05T00:00:00Z')
   * Mar 05, 2023
   * @param {{string|null|undefined}} {{dateString}} - The input date string to format; may be null, undefined, '-' or any value parseable by Date.
   * @returns {{string}} Formatted date as 'Mon DD, YYYY', '-' for empty indicator, or the original input if parsing fails.
   **/
  formatLastVisitDate(dateString: string | null | undefined): string {
    if (!dateString || dateString === '-') return '-';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      const month = monthNames[date.getMonth()];
      const day = date.getDate().toString().padStart(2, '0');
      const year = date.getFullYear();

      return `${month} ${day}, ${year}`;
    } catch (error) {
      console.warn('Error formatting last visit date:', dateString);
      return dateString;
    }
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
